/** 项目工程联络：业主/项目团队与已签约分包商分组展示，复用系统电话、短信和邮件。
 * 独立组件用于管理实时刷新、项目切换及手机弹层，避免联络资料滞留在旧项目上。
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Camera, ChevronDown, Loader2, Phone, RefreshCw, X } from "lucide-react";
import { authStore } from "../../lib/api";
import { T } from "../../lib/i18n";
import { uploadChunked } from "../../lib/upload";
import { shrinkImage } from "../../lib/imageShrink";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import ContactActions from "../ContactActions";
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const AV_BG = ["bg-violet-500", "bg-emerald-500", "bg-pink-500", "bg-slate-500", "bg-blue-500", "bg-amber-500"];

function ContactAvatar({ c, zh }) {
  const tok = authStore.get("ali_token") || "";
  return (
    <div className="relative shrink-0" data-testid={`hub-contact-avatar-${c.key}`}>
      {c.avatarFileId
        ? <img src={`${BACKEND}/api/files/${c.avatarFileId}/download?auth=${tok}`} alt={c.name} className="h-9 w-9 rounded-full object-cover border border-slate-200" />
        : <div className={`h-9 w-9 rounded-full ${AV_BG[(c.name || "?").charCodeAt(0) % AV_BG.length]} text-white text-xs font-bold flex items-center justify-center`}>{(c.name || "?")[0].toUpperCase()}</div>}
      {c.online !== null && c.online !== undefined && (
        <span data-testid={`hub-contact-online-${c.key}`} title={c.online ? (zh ? "在线" : "Online") : (zh ? "离线" : "Offline")}
          className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white ${c.online ? "bg-emerald-500" : "bg-slate-300"}`} />
      )}
    </div>
  );
}

const CONTACT_EDGE = 8;        // 面板距视口边缘的留白

export default function ContactDrop({ api, id, zh, user }) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(null);
  const [subcontractors, setSubcontractors] = useState([]);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState("subcontractors");
  const requestSeq = useRef(0);
  const [upKey, setUpKey] = useState(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  // 面板位置（fixed 定位，坐标相对视口）。null = 尚未测量：面板先渲染在屏外（可测量但不可见），
  // useLayoutEffect 量完立刻写回，浏览器绘制前完成，用户看不到这一帧。
  const [pos, setPos] = useState(null);
  useEffect(() => {
    if (!open) return;
    // 发送窗口拥有自己的关闭行为；Esc 先关闭短信/邮件，不能连同联络栏一起卸载而丢失编辑内容。
    const h = (e) => { if (e.key === "Escape" && !e.target.closest?.('[role="dialog"]')) { setOpen(false); btnRef.current?.querySelector('button')?.focus(); } };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open]);
  // 面板改为「portal 到 body + fixed 定位」（2026-10-16）。
  // 起因：项目详情常以抽屉形态打开（GlobalProjectDrawer → CrmDetailDrawer），抽屉内容区是
  // `overflow-y-auto overflow-x-hidden` 的滚动容器；原 `absolute right-0` 的面板向左溢出按钮后
  // 会被该容器直接裁掉（表现为弹窗左侧一截看不见）。
  // 做法对齐 components/ContextMenu.jsx：脱离裁剪容器，位置按按钮 rect 现算 ——
  // 右缘对齐按钮并限制在视口内；下方不足 240px 时向上展开，手机宽度最多为视口减 16px。
  // 宽度实测，不假设全站字号固定；字体缩放和界面密度都会影响 rem 对应的真实像素。
  const place = useCallback(() => {
    const el = btnRef.current, pe = panelRef.current;
    if (!el) return;
    const w = pe ? pe.offsetWidth : 0;
    const r = el.getBoundingClientRect();
    const below = window.innerHeight - r.bottom - CONTACT_EDGE - 4;
    const maxH = Math.min(Math.round(window.innerHeight * 0.75), below < 240 && r.top > 240 ? r.top - CONTACT_EDGE - 4 : below);
    const top = below < 240 && r.top > 240 ? r.top - maxH - 4 : r.bottom + 4;
    const left = w
      ? Math.max(CONTACT_EDGE, Math.min(r.right - w, window.innerWidth - w - CONTACT_EDGE))
      : Math.max(CONTACT_EDGE, r.right - 360);   // 首帧宽度兜底，下一帧用面板实测值校正。
    setPos({ left, top, maxH: Math.max(100, maxH) });
  }, []);
  // 用 useLayoutEffect（绘制前测量，避免面板先闪到屏外再跳回来）；下一帧再校一次 ——
  // 抽屉有 slide-in 入场动画，若在动画途中打开，首帧量到的按钮位置会偏右，二测拿到稳定位置。
  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    place();
    const raf = requestAnimationFrame(place);
    const onWin = () => place();
    window.addEventListener("resize", onWin);
    // 捕获阶段监听滚动：抽屉内容区自身的滚动不会冒泡到 window，必须用 capture 才能跟随重算
    window.addEventListener("scroll", onWin, true);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", onWin); window.removeEventListener("scroll", onWin, true); };
  }, [open, place]);
  // 每次展开读取实时公司资料；序号隔离切换项目、关闭重开和弱网返回，避免旧号码覆盖新项目。
  useEffect(() => {
    requestSeq.current += 1; setOpen(false); setList(null); setSubcontractors([]); setError(false);
    return () => { requestSeq.current += 1; };
  }, [id]);
  const loadContacts = async () => {
    const seq = ++requestSeq.current;
    setError(false); setList(null); setSubcontractors([]);
    try {
      const { data } = await api.get(`/projects/${id}/hub/contacts`);
      if (seq !== requestSeq.current) return;
      setList(data.contacts || []); setSubcontractors(data.subcontractors || []);
    } catch {
      if (seq === requestSeq.current) setError(true);
    }
  };
  const toggle = () => {
    const o = !open;
    setOpen(o);
    if (o) loadContacts();
  };
  const canSet = (c) => !!c.username && (["admin", "ceo", "eng_manager"].includes(user?.role) || user?.username === c.username);
  const uploadAvatar = async (c, file) => {
    if (!(file.type || "").startsWith("image/")) { toast.error(zh ? "头像必须是图片文件" : "Image files only"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error(zh ? "头像图片请小于 5MB" : "Max 5MB"); return; }
    setUpKey(c.key);
    try {
      const fin = await uploadChunked(api, await shrinkImage(file, { maxSide: 1024, minBytes: 300 * 1024 }), { type: file.type || "image/jpeg" });
      await api.post(`/users/${c.username}/avatar`, { fileId: fin.fileId });
      toast.success(zh ? "头像已更新" : "Avatar updated");
      await loadContacts();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "头像上传失败" : "Upload failed")); }
    setUpKey(null);
  };
  return (
    <div className="relative" ref={btnRef}>
      <Button data-testid="hub-contact-btn" size="sm" variant="outline" onClick={toggle} aria-expanded={open} className="min-h-11 sm:min-h-8 text-xs"><Phone className="h-3.5 w-3.5 mr-1" />{zh ? "工程联络" : "Contacts"}<ChevronDown className="h-3 w-3 ml-1" /></Button>
      {open && createPortal(
        <>
          {/* 透明遮罩：点击面板外任意处关闭。z-[59] 介于抽屉（z-45~49）与面板（z-[60]）之间 */}
          <div className="fixed inset-0 z-[59] pointer-events-auto" onClick={() => setOpen(false)} />
          <div ref={panelRef}
            className="fixed z-[60] pointer-events-auto bg-white rounded-xl shadow-xl border border-slate-200 p-3 w-96 max-w-[calc(100vw-16px)] space-y-3 overflow-y-auto overscroll-contain"
            style={{ left: pos ? pos.left : -9999, top: pos ? pos.top : -9999, maxHeight: pos ? pos.maxH : "70vh", visibility: pos ? "visible" : "hidden" }}
            data-testid="hub-contact-dropdown">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold">{zh ? "工程联络" : "Project contacts"}</p>
              <div className="flex gap-2"><Button size="icon" variant="ghost" onClick={loadContacts} aria-label={zh ? "刷新联络信息" : "Refresh contacts"} className="h-11 w-11"><RefreshCw className="h-4 w-4" /></Button><Button size="icon" variant="ghost" onClick={() => { setOpen(false); btnRef.current?.querySelector('button')?.focus(); }} aria-label={zh ? "关闭工程联络" : "Close contacts"} className="h-11 w-11"><X className="h-4 w-4" /></Button></div>
            </div>
            <div className="flex gap-2 border-b border-slate-100 pb-2" aria-label={zh ? "联络分类" : "Contact groups"}>
              {[['subcontractors', zh ? '已签约分包商' : 'Signed subcontractors', subcontractors.length], ['team', zh ? '业主与项目团队' : 'Owner & project team', list?.length || 0]].map(([key, label, count]) => <button key={key} type="button" aria-pressed={tab === key} onClick={() => setTab(key)} className={`min-h-11 flex-1 rounded-lg px-2 text-xs font-medium ${tab === key ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}>{label}{list && ` (${count})`}</button>)}
            </div>
            {error ? <div role="alert" className="py-4 text-sm text-slate-600"><p>{zh ? "联络信息加载失败，请重试。" : "Could not load contacts. Please retry."}</p><Button variant="outline" className="mt-2 min-h-11" onClick={loadContacts}>{zh ? "重试" : "Retry"}</Button></div> : !list && <div role="status" aria-label={zh ? "正在加载联络信息" : "Loading contacts"} className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>}
            {list && tab === "subcontractors" && (subcontractors.length ? subcontractors.map(company => <section key={company.key} data-testid={`hub-subcontractor-${company.key}`} className="rounded-lg border border-slate-200 p-3 space-y-3">
              <div><h3 className="break-words text-sm font-semibold text-slate-800">{company.name || (zh ? '未登记公司名称' : 'Company name not recorded')}</h3><p className="mt-1 break-words text-xs text-slate-500">{company.trades.map(t => T(t.name || t.key)).join(' · ')}</p></div>
              {company.contacts.length ? company.contacts.map((contact, index) => <div key={index} className="space-y-2 border-t border-slate-100 pt-2">
                <p className="break-words text-sm font-medium text-slate-700">{contact.name || (zh ? '公司联络' : 'Company contact')}{contact.title && <span className="ml-2 text-xs font-normal text-slate-500">{contact.title}</span>}</p>
                {contact.phone && <p className="break-all text-xs text-slate-600 select-text">{contact.phone}</p>}
                {contact.email && <p className="break-all text-xs text-slate-600 select-text">{contact.email}</p>}
                {contact.phone || contact.email ? <ContactActions panelMode phone={contact.phone} email={contact.email} name={contact.name || company.name} variant="map" idBase={`hub-sub-${company.key}-${index}`} /> : <p className="text-xs text-slate-500">{zh ? '未登记电话或邮箱，请在分包商管理中补充。' : 'Add a phone or email in subcontractor management.'}</p>}
              </div>) : <p className="text-xs text-slate-500">{zh ? '未登记联系方式，请在分包商管理中补充。' : 'Add contact details in subcontractor management.'}</p>}
            </section>) : <p className="py-4 text-sm text-slate-500">{zh ? '本项目暂无已签约分包商。完成签署或上传已签合同后将自动显示。' : 'No signed subcontractors yet. They will appear after signing or uploading a signed contract.'}</p>)}
            {tab === "team" && (list || []).map((c) => (
              <div key={c.key} className="text-xs border-b last:border-0 border-slate-50 pb-2 last:pb-0" data-testid={`hub-contact-${c.key}`}>
                <div className="flex items-start gap-2.5">
                  <ContactAvatar c={c} zh={zh} />
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] text-slate-400">{T(c.role)}</p>
                    <p className="font-semibold text-slate-700 flex items-center gap-1.5 flex-wrap">{c.name}
                      {c.online === true && <span className="text-[9px] font-bold text-emerald-600" data-testid={`hub-contact-online-label-${c.key}`}>{zh ? "在线" : "Online"}</span>}
                      {c.online === false && <span className="text-[9px] text-slate-300" data-testid={`hub-contact-offline-label-${c.key}`}>{zh ? "离线" : "Offline"}</span>}
                    </p>
                    {(c.phone || c.email)
                      ? <div className="mt-1"><ContactActions panelMode phone={c.phone} email={c.email} name={c.name} variant="map" idBase={`hub-ct-${c.key}`} /></div>
                      : <p className="text-[10px] text-slate-300 mt-0.5">{zh ? "未登记联系方式 — 可在「编辑项目」或员工档案补充" : "No contact info"}</p>}
                  </div>
                  {canSet(c) && (
                    <label className="shrink-0 h-6 w-6 rounded-full border border-slate-200 flex items-center justify-center cursor-pointer text-slate-400 hover:text-indigo-600 hover:border-indigo-300 transition-colors"
                      title={zh ? "上传员工照片" : "Upload photo"} data-testid={`hub-contact-avatar-upload-${c.key}`}>
                      {upKey === c.key ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}
                      <input type="file" accept="image/*" className="hidden" disabled={!!upKey}
                        onChange={(e) => { if (e.target.files?.[0]) uploadAvatar(c, e.target.files[0]); e.target.value = ""; }} />
                    </label>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

